<template lang="pug">
    include ../../icon/arrow

    div(
        class="_pgn no-wrap",
        role="tablist",
        :style="{ width }")
        div
            ib-button(
                class="fs7 link mirror-rtl",
                :disabled="page <= min",
                @click="start")
                +icon-arrow-double_left_filled
            ib-button(
                class="fs7 link mirror-rtl",
                :disabled="page <= min",
                @click="prev")
                +icon-arrow-back
        div(ref="page", class="_pgl")
            a(
                v-for="p in count",
                role="tab",
                :aria-selected="first + p === page",
                @click="goto(first + p)",
                v-text="first + p")
        div
            ib-button(
                class="fs7 link mirror-rtl",
                :disabled="page >= max",
                @click="next")
                +icon-arrow-forward
            ib-button(
                class="fs7 link mirror-rtl",
                :disabled="page >= max",
                @click="end")
                +icon-arrow-double_right_filled
</template>

<script>
    import resize from '../private/resize';
    import telemetry from '../private/telemetry';
    import $util from '../../prototype/util';

    const INT_MAX = 2e9;

    export default {
        name: 'ib-paginate',

        mixins: [
            resize,
            telemetry
        ],

        props: {
            routeParam: String,
            value: Number,
            /**
             * Lowest page number to display.
             */
            min: {
                type: Number,
                default: 1
            },
            /**
             * Highest page number to display.
             */
            max: {
                type: Number,
                required: true
            },
            /**
             * Maximum number of pages to navigate using rewind and fast-forward buttons.
             * By default, these buttons will go to the first and last pages respectively.
             */
            stride: Number,
            /**
             * Width in any CSS units.
             */
            width: String,
            /**
             * Width in visible page numbers. This is an alternative to `width`.
             */
            size: Number
        },

        data() {
            return {
                page: 0,
                pmax: 0,
                rseW: this.size == null,
                rseH: false
            };
        },

        computed: {
            first() {
                return Math.max(this.min, Math.min(this.max - this.count + 1, this.page - Math.floor(this.count / 2))) - 1 | 0;
            },

            count() {
                return Math.min(
                    this.stride || INT_MAX,
                    this.size || this.pmax,
                    this.max - this.min + 1);
            }
        },

        watch: {
            value: {
                handler(value) {
                    if (!this.routeParam) {
                        this.page = Math.max(this.min, Math.min(this.max, value | 0));
                    }
                },
                immediate: true
            },

            $route: {
                handler(value) {
                    if (this.routeParam) {
                        this.page = this.$route.params[this.routeParam] | 0;
                    }
                },
                immediate: true
            }
        },

        methods: {
            resize() {
                const page = this.$refs.page;
                this.pmax = page.offsetWidth / (parseFloat($util.css(page, 'font-size')) * 2);
            },

            prev() {
                this.goto(this.page - 1);
            },

            next() {
                this.goto(this.page + 1);
            },

            start() {
                this.goto(this.stride ? this.page - this.stride : this.min);
            },

            end() {
                this.goto(this.stride ? this.page + this.stride : this.max);
            },

            goto(page) {
                page = Math.max(this.min, Math.min(this.max, page | 0));
                if (page !== this.page) {
                    this.page = page;
                    this.$emit('input', page);
                    if (this.routeParam) {
                        let params = {};
                        params[this.routeParam] = page;
                        this.$router.replace({params});
                    }
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._pgn {
        display: flex;
        align-items: center;

        > :first-child {
            -webkit-margin-start: auto;
            margin-inline-start: auto;
        }
        > :last-child {
            -webkit-margin-end: auto;
            margin-inline-end: auto;
        }
    }
    ._pgl {
        margin: 0 4px;
        > a {
            display: inline-block;
            width: 2em;
            text-align: center;
            font-weight: 500;
            color: @fg;
            opacity: .42;
            cursor: pointer;
            padding: 16px 4px 10px;
            border-bottom: 4px solid transparent;
            &:hover {
                opacity: .6;
            }
            &[aria-selected=true] {
                font-weight: 600;
                border-color: @fg-accent;
                opacity: 1;
            }
        }
    }
</style>
